import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';

interface OnboardingStep {
  title: string;
  description: string;
  targetElement?: string;
  highlightArea?: 'tabs' | 'favorites' | 'search' | 'sections';
}

const onboardingSteps: OnboardingStep[] = [
  {
    title: 'Welcome to the New Navigation!',
    description: 'We\'ve reorganized the menu to make it easier to find what you need. Let\'s take a quick tour.',
    highlightArea: 'tabs',
  },
  {
    title: 'Navigation Tabs',
    description: 'The menu is now organized into 3 main tabs: Operations (day-to-day work), Intelligence (AI & analytics), and Administration (finance & settings).',
    highlightArea: 'tabs',
  },
  {
    title: 'Favorites & Most Used',
    description: 'Right-click any menu item to add it to favorites. The system also tracks your most-used pages automatically.',
    highlightArea: 'favorites',
  },
  {
    title: 'Quick Search',
    description: 'Use the search box or press "/" to quickly find any page. Press Cmd/Ctrl+K for the command palette.',
    highlightArea: 'search',
  },
  {
    title: 'Collapsible Sections',
    description: 'Click section headers to expand/collapse them. Right-click to pin sections to keep them always open.',
    highlightArea: 'sections',
  },
  {
    title: 'All Set!',
    description: 'You\'re ready to go! Explore the new navigation and discover more features as you work.',
  },
];

export function NavigationOnboarding() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has seen the onboarding
    const hasSeenOnboarding = localStorage.getItem('nav-onboarding-completed');
    if (!hasSeenOnboarding) {
      setIsVisible(true);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('nav-onboarding-completed', 'true');
    setIsVisible(false);
  };

  const handleComplete = () => {
    localStorage.setItem('nav-onboarding-completed', 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  const step = onboardingSteps[currentStep];
  const isLastStep = currentStep === onboardingSteps.length - 1;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 backdrop-blur-sm" />

      {/* Onboarding Card */}
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md">
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-bold">{step.title}</h3>
              <button
                onClick={handleSkip}
                className="p-1 hover:bg-white/20 rounded-full transition"
                aria-label="Skip tour"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-white/90">{step.description}</p>
          </div>

          {/* Progress */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500">
                Step {currentStep + 1} of {onboardingSteps.length}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                style={{
                  width: `${((currentStep + 1) / onboardingSteps.length) * 100}%`,
                }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="text-sm text-gray-600 hover:text-gray-800 transition"
            >
              Skip Tour
            </button>
            <div className="flex items-center space-x-2">
              {currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                  className="flex items-center space-x-1 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>
              )}
              <button
                onClick={handleNext}
                className="flex items-center space-x-1 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 rounded-lg transition shadow-sm"
              >
                <span>{isLastStep ? 'Get Started' : 'Next'}</span>
                {isLastStep ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}




























